import {inject, Injectable, NgZone, OnDestroy} from '@angular/core';
import {Router} from '@angular/router';
import {fromEvent, merge, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {environment} from '../../../environments/environment';

/** After inactivity, navigates to Home (legacy session-timeout behavior). */
@Injectable({providedIn: 'root'})
export class SessionTimeoutService implements OnDestroy {
  readonly #router = inject(Router);
  readonly #zone = inject(NgZone);
  #timeoutId: ReturnType<typeof setTimeout> | undefined;
  #activitySub: Subscription | undefined;

  start(): void {
    this.stop();
    const minutes = environment.sessionTimeoutMinutes;
    if (!minutes || minutes <= 0) {
      return;
    }

    this.#zone.runOutsideAngular(() => {
      this.#activitySub = merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'click'),
        fromEvent(document, 'scroll'),
        fromEvent(document, 'touchstart'),
      )
        .pipe(debounceTime(500))
        .subscribe(() => this.#resetTimer(minutes));

      this.#resetTimer(minutes);
    });
  }

  stop(): void {
    this.#activitySub?.unsubscribe();
    this.#activitySub = undefined;
    if (this.#timeoutId) {
      clearTimeout(this.#timeoutId);
      this.#timeoutId = undefined;
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }

  #resetTimer(minutes: number): void {
    if (this.#timeoutId) {
      clearTimeout(this.#timeoutId);
    }
    this.#timeoutId = setTimeout(
      () => this.#zone.run(() => void this.#router.navigateByUrl('/home')),
      minutes * 60_000,
    );
  }
}
